import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Divider,
  Paper,
  List,
} from '@mui/material';
import { toast } from '../../../../../../ui/toast';
import { Drawer, ListItemButton } from '../../../../../../ui';
import { addQuestionToAssessment } from '../../../../../../utils/api_service/assessment_api';
import {
  Question,
  QuestionSearch,
  SubSkill,
} from '../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { Filter } from './Components';
import { useSelectQuestions } from './Hooks';

interface ISelectQuestions {
  isOpen: boolean;
  onCloseDrawer: () => void;
  onSave: (finalSelection: Question[]) => void;
  assessmentId: string;
  skillList: SelectOptions[] | undefined;
  levelList: SelectOptions[] | undefined;
  subSkillList: SubSkill[] | undefined;
  jobTitleList: SelectOptions[] | undefined;
  initialSelected: Question[];
  onAddQuestion: (q: Question) => void;
}

export const SelectQuestions: React.FC<ISelectQuestions> = ({
  isOpen,
  onCloseDrawer,
  onSave,
  assessmentId,
  skillList,
  levelList,
  subSkillList,
  jobTitleList,
  initialSelected,
  onAddQuestion,
}) => {
  const [searchObject, setSearchObject] = useState<QuestionSearch>({
    questionTitle: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    jobTitle: '',
    assessmentId,
  });

  const {
    questionsList,
    selectedQuestions,
    loading,
    totalCount,
    pageNumber,
    totalPages,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    goToNextPage,
    goToPrevPage,
    setIsSaving,
    isSaving,
  } = useSelectQuestions({ searchObject, initialSelected });

  const handleSaveClick = async () => {
    setIsSaving(true);

    // Find only the newly-selected ones:
    const toAdd = selectedQuestions.filter(
      (q) => !initialSelected.some((s) => s.id === q.id),
    );

    try {
      await Promise.all(
        toAdd.map((q) =>
          addQuestionToAssessment('', q.id, assessmentId),
        ),
      );
      toast.success('Questions added successfully!');
      // Now notify parent of the final array and close
      onSave(selectedQuestions);
    } catch (err) {
      toast.error('Failed to add some questions.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      width="w-full md:w-[800px]"
      title="Invite Questions"
      footerContent={
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Button
            variant="outlined"
            onClick={onCloseDrawer}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isSaving ? 'Saving' : 'Save'}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ display: 'flex', gap: 3, height: '80vh' }}>
        {/* Filter pane */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          <Filter
            skillList={skillList || []}
            levelList={levelList || []}
            subSkillList={subSkillList}
            jobTitleList={jobTitleList}
            searchObject={searchObject}
            setSearchObject={setSearchObject}
          />
        </Box>

        {/* Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderStyle: 'dashed' }}
        />

        {/* Main content: top and bottom halves */}
        <Stack spacing={2} sx={{ flex: 1, height: '100%', minWidth: 0 }}>
          {/* Top half: Available Questions */}
          <Stack sx={{ height: '50%', minHeight: 0 }} spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1" fontWeight={600}>
                All Questions
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  handleSelectAll();
                  questionsList.forEach(onAddQuestion);
                }}
                disabled={questionsList.length === 0}
                sx={{ textTransform: 'none' }}
              >
                Select All
              </Button>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Page {pageNumber} of {totalPages} ({totalCount} total)
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={goToPrevPage}
                  disabled={pageNumber === 1 || loading}
                  sx={{ textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={goToNextPage}
                  disabled={pageNumber === totalPages || loading}
                  sx={{ textTransform: 'none' }}
                >
                  Next
                </Button>
              </Stack>
            </Stack>

            <Paper
              variant="outlined"
              sx={{ flex: 1, overflowY: 'auto', p: 1, borderRadius: 1.5 }}
            >
              {loading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 1 }}
                >
                  Loading…
                </Typography>
              ) : questionsList.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 1 }}
                >
                  No questions available
                </Typography>
              ) : (
                <List
                  dense
                  disablePadding
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {questionsList.map((q) => (
                    <ListItemButton
                      key={q.id}
                      onClick={() => {
                        handleSelect(q);
                        onAddQuestion(q);
                      }}
                    >
                      {q.question || 'Untitled Question'}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Paper>
          </Stack>

          {/* Divider line */}
          <Divider />

          {/* Bottom half: Selected Questions */}
          <Stack sx={{ height: '50%', minHeight: 0 }} spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Selected Questions
              </Typography>
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={handleRemoveAll}
                disabled={selectedQuestions.length === 0}
                sx={{ textTransform: 'none' }}
              >
                Remove All
              </Button>
            </Stack>

            <Paper
              variant="outlined"
              sx={{ flex: 1, overflowY: 'auto', p: 1, borderRadius: 1.5 }}
            >
              {selectedQuestions.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 1 }}
                >
                  No questions selected
                </Typography>
              ) : (
                <List
                  dense
                  disablePadding
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {selectedQuestions.map((q) => (
                    <ListItemButton
                      key={q.id}
                      isSelected
                      onClick={() => {}}
                      onRemove={() => handleRemove(q)}
                    >
                      {q.question || 'Untitled Question'}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Paper>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default SelectQuestions;
